import { applyColumn, transformSteps, collapseTransform } from './transform_core.js';
import { detectSeparator, stripBom } from './csv_dialect.js';
import { resolveDatasets, datasetOfCube } from './datasets.js';

export { transformSteps, collapseTransform };

export const TREATMENT_MATRIX = {
  alone: { personal: 'drop', confidential: 'drop', none: 'drop' },
  combined: { personal: 'coarsen', confidential: 'coarsen', none: 'coarsen' },
  no: { personal: 'isolate', confidential: 'isolate', none: 'open' },
};

export const deriveTreatment = (identifies, sensitivity) =>
  TREATMENT_MATRIX[identifies]?.[sensitivity] ?? null;

// ── Catalog terms and disclosure labels ────────────────────────────────────
// A catalog term already declares the record class it belongs to and a suggested
// posture, so choosing the term is the analyst's single real decision: the record
// follows from it and both labels are prefilled. Whatever they change afterwards
// is an override, and is recorded as one.

export const findTerm = (catalog, id) => catalog?.terms?.find((term) => term.id === id) ?? null;

// Ingest mappings historically used `target` as the shared-term field. The
// authoring UI also keeps `term` so it can distinguish a catalog choice from a
// hand-entered term. Hydrate that UI-only alias at the draft boundary rather
// than making a valid, provisioned mapping look unfinished.
export function prepareMappingDraft(mapping) {
  const draft = structuredClone(mapping);
  for (const column of draft?.columns ?? []) {
    if (column.record !== 'shared' && !column.term && column.target) column.term = column.target;
  }
  return draft;
}

export function termPosture(term) {
  if (!term) return null;
  const { identifies, sensitivity } = term.posture;
  return { identifies, sensitivity, treatment: term.posture.treatment ?? deriveTreatment(identifies, sensitivity) };
}

// Most terms belong to one record class. Measures and cohort dimensions can be
// valid on sibling record types; in that case retain the analyst's compatible
// current record, falling back to the catalog's first declared class.
function recordForTerm(column, term) {
  const records = Array.isArray(term.record) ? term.record : [term.record];
  return records.includes(column.record) ? column.record : records[0];
}

export function applyCatalogTerm(column, term) {
  const posture = termPosture(term);
  Object.assign(column, { term: term.id, target: term.id, record: recordForTerm(column, term), ...posture });
  delete column.manualTerm;
  delete column.postureOverride;
  delete column.treatmentOverride;
  return column;
}

// Changing the term after the labels were deliberately overridden should not
// silently discard that judgement, so the caller can offer to carry it across.
// Whether it still counts as an override is re-decided against the new term.
export function retermKeepingLabels(column, term, catalog) {
  Object.assign(column, { term: term.id, target: term.id, record: recordForTerm(column, term) });
  delete column.manualTerm;
  return syncOverrideFlags(column, catalog);
}

// A compact term (prefix:localName) or an absolute IRI. Checked rather than
// inferred, so "Rider" is rejected instead of quietly becoming a term.
const COMPACT_TERM = /^[A-Za-z][A-Za-z0-9_.-]*:[A-Za-z0-9_][A-Za-z0-9_.\-%]*$/;
const ABSOLUTE_IRI = /^https?:\/\/\S+$/;
export const isValidTermId = (id) => {
  const value = String(id ?? '').trim();
  return COMPACT_TERM.test(value) || ABSOLUTE_IRI.test(value);
};

// A hand-entered term has no catalog guidance, so its labels are authored, not
// overridden. An unlabelled column starts cautious rather than open.
export function applyManualTerm(column, id, record) {
  Object.assign(column, { term: id, target: id, record, manualTerm: true });
  column.identifies ??= 'no';
  column.sensitivity ??= 'personal';
  if (!column.treatmentOverride) column.treatment = deriveTreatment(column.identifies, column.sensitivity);
  delete column.postureOverride;
  return column;
}

// A posture override means the two labels differ from the catalog's suggestion.
// A treatment override means the labels were accepted but the treatment they
// derive was not — a different act by the analyst, so recorded separately.
export function overridesFor(column, catalog) {
  const posture = column.manualTerm ? null : termPosture(findTerm(catalog, column.term));
  const changed = Boolean(posture)
    && (column.identifies !== posture.identifies || column.sensitivity !== posture.sensitivity);
  const suggested = posture && !changed ? posture.treatment : deriveTreatment(column.identifies, column.sensitivity);
  return { posture: changed, treatment: column.treatment !== suggested, suggested };
}

export function syncOverrideFlags(column, catalog) {
  const { posture, treatment } = overridesFor(column, catalog);
  if (posture) column.postureOverride = true; else delete column.postureOverride;
  if (treatment) column.treatmentOverride = true; else delete column.treatmentOverride;
  return column;
}

export function setPosture(column, { identifies, sensitivity }, catalog) {
  if (identifies !== undefined) column.identifies = identifies;
  if (sensitivity !== undefined) column.sensitivity = sensitivity;
  column.treatment = deriveTreatment(column.identifies, column.sensitivity);
  return syncOverrideFlags(column, catalog);
}

export function setTreatment(column, treatment, catalog) {
  column.treatment = treatment;
  return syncOverrideFlags(column, catalog);
}

export function resetPosture(column, catalog) {
  const term = findTerm(catalog, column.term);
  return term ? applyCatalogTerm(column, term) : column;
}

// A term may belong to a record class the mapping does not have yet. Its
// records go where its dataset's do (lib/datasets.js, recordContainer); only
// the raw rows' base is the source's, shared with its other classes.
export function defaultRecordLocation(recordId, mapping) {
  const raw = Object.values(mapping?.records ?? {})[0]?.raw;
  return raw ? { raw } : {};
}

export function ensureRecord(mapping, recordId) {
  if (mapping.records[recordId]) return false;
  mapping.records[recordId] = defaultRecordLocation(recordId, mapping);
  return true;
}

// How a mapping and a loaded CSV disagree. Both directions matter and only one
// of them was ever checked: a header the mapping has not accounted for is
// visible as a new column, but a *column whose source is absent from the file*
// is silent — it reads `undefined`, transforms an empty string, and publishes
// blanks. Re-assessing an updated export is exactly when that happens.
export function reconcile(mapping, headers) {
  const present = new Set(headers ?? []);
  const accounted = new Set([
    ...(mapping?.columns ?? []).filter((c) => c.record !== 'shared').map((c) => c.source),
    ...(mapping?.drop ?? []),
  ]);
  return {
    // No headers means no file has been loaded, not a file that happens to be
    // missing every column. Absence of evidence: with nothing to compare the
    // mapping against there is nothing to report, and saying otherwise would
    // accuse a perfectly good mapping the moment the screen opens.
    missing: present.size ? [...accounted].filter((source) => !present.has(source)) : [],
    unaccounted: (headers ?? []).filter((header) => !accounted.has(header)),
  };
}

// The record types a mapping actually uses, derived rather than authored: a
// record exists because some column's term lands in it. Entries with no columns
// are still listed — they are leftovers from an earlier draft, and listing them
// is what makes them removable.
export function recordSummary(mapping) {
  const columns = (mapping?.columns ?? []).filter((c) => c.record !== 'shared');
  return Object.entries(mapping?.records ?? {}).map(([id, location]) => ({
    id,
    container: location?.container ?? '',
    columns: columns.filter((c) => c.record === id),
  }));
}

// What a column still needs before it can be published: no term to publish it
// under, a source the loaded file does not have, or a transform that cannot run.
export function columnProblems(column, headers = []) {
  const found = validateTransform(column.transform);
  if (!column.term) found.unshift('needs a shared term');
  if (headers.length && !headers.includes(column.source)) found.unshift('not in the loaded file');
  return found;
}

// ── Aggregate authoring ─────────────────────────────────────────────────────

// Transforms that end with fewer distinct values than they started with.
// `direct` passes values through untouched and `split` turns one value into
// several, so a chain ending in either has not coarsened anything, whatever the
// column is labelled. Structural rather than measured: the Queries screen has no
// sample rows to count distinct values in.
const REDUCING_TRANSFORMS = new Set(['mapValues', 'bucket', 'dateBucket', 'rules', 'const']);
export function coarsens(transform) {
  const steps = transformSteps(transform);
  return steps.length ? REDUCING_TRANSFORMS.has(steps.at(-1).fn) : false;
}

// Whether a labelled column may become a cube dimension, and if not, why —
// because a dimension that silently vanishes from the picker reads as a bug, and
// the reason is usually a labelling decision the analyst can go back and change.
//
// This is the authoring guard, not the validation rule. `validateAggregations`
// deliberately keeps accepting a dimension this refuses: Sala Apolo's shipped
// `pronounsByPeriodCube` uses a `coarsen` column whose transform only splits, and
// a validator that rejected the config it ships would break the demo it protects.
export function dimensionEligibility(mapping) {
  return (mapping?.columns ?? []).filter((c) => c.record !== 'shared').map((column) => ({
    target: column.target, source: column.source, treatment: column.treatment,
    ...eligibilityOf(column),
  }));
}

function eligibilityOf(column) {
  if (!column.target) return { eligible: false, reason: 'has no shared term yet' };
  if (column.treatment === 'drop') return { eligible: false, reason: 'is dropped, so it never reaches the Pod' };
  if (column.treatment === 'coarsen' && !coarsens(column.transform))
    return { eligible: false, reason: 'is labelled coarsen, but its transform does not reduce values' };
  return { eligible: true, reason: null };
}

export const eligibleDimensions = (mapping) => {
  const seen = new Set();
  return dimensionEligibility(mapping).filter((dimension) => {
    if (!dimension.eligible || seen.has(dimension.target)) return false;
    seen.add(dimension.target);
    return true;
  });
};

// Why these two dimensions cannot be crossed, or null. Isolated dimensions are
// the ones that identify someone when combined with another, which is the whole
// of what a cross does.
export function combineProblem(a, b) {
  if (!a || !b) return 'Choose two dimensions.';
  if (a.target === b.target) return 'Choose two different dimensions.';
  if (a.treatment === 'isolate' && b.treatment === 'isolate')
    return `${a.target} and ${b.target} are both isolated: crossing two isolated dimensions is what isolation forbids.`;
  return null;
}

// Eligible dimensions with no single-dimension aggregate of their own. Offered,
// never applied: seeding these on load would create cubes the analyst never
// asked for, and against the shipped CTL config it would silently add two.
export function missingMarginals(mapping, aggregations) {
  const covered = new Set((aggregations?.aggregates ?? [])
    .filter((a) => a.dimensions?.length === 1).map((a) => a.dimensions[0]));
  return eligibleDimensions(mapping).filter((d) => !covered.has(d.target));
}

const localName = (target) => String(target).replace(/^[^:]+:/, '').replace(/[^A-Za-z0-9]+/g, '');
const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// An id in the house style of the shipped config: `regionByPeriodCube`,
// `genderNationalityCube`. A suggestion the analyst may overwrite, not a rule.
export function suggestAggregateId(dimensions = []) {
  const [first, second] = dimensions.map(localName);
  if (!first) return '';
  return second ? `${first}${capitalize(second)}Cube` : `${first}ByPeriodCube`;
}

// Checked as it is typed, not on the click that adds: an id that collides is a
// silent overwrite on the server, where saveAggregate upserts by id.
export function validateAggregateId(id, aggregations, { allow = null } = {}) {
  if (!id) return 'A cube needs an id.';
  if (!/^[A-Za-z][A-Za-z0-9-]*$/.test(id)) return 'Use letters, digits and hyphens, starting with a letter.';
  if ((aggregations?.aggregates ?? []).some((a) => a.id === id && a.id !== allow))
    return 'That cube id already exists.';
  return null;
}

// Removing an aggregate that governance has already granted would revoke a
// partner's access from the analyst's screen, which is not the analyst's act to
// make. Unpublishing comes first, and it happens on Partners.
export function removalProblem(aggregate) {
  const mode = aggregate?.sharing?.mode ?? 'none';
  if (mode === 'none') return null;
  return `${aggregate.id} is shared (${mode}). Governance has to withdraw it on Partners before it can be removed here.`;
}

// Reviewing the saved JSON should be stable however the analyst got there, so
// columns and drops are always written in source-file order. Shared housekeeping
// columns keep their leading position; they are not source columns at all.
export function sortBySourceOrder(mapping, headers) {
  const rank = new Map(headers.map((header, i) => [header, i]));
  const at = (source) => rank.get(source) ?? Number.MAX_SAFE_INTEGER;
  mapping.columns.sort((a, b) =>
    (a.record === 'shared' ? -1 : at(a.source)) - (b.record === 'shared' ? -1 : at(b.source)));
  mapping.drop.sort((a, b) => at(a) - at(b));
  return mapping;
}

function parseCsv(csvText, separator) {
  const table = [];
  let row = [], cell = '', quoted = false;
  for (let i = 0; i < csvText.length; i++) {
    const char = csvText[i];
    if (quoted) {
      if (char === '"' && csvText[i + 1] === '"') { cell += '"'; i++; }
      else if (char === '"') quoted = false;
      else cell += char;
    } else if (char === '"' && cell === '') quoted = true;
    else if (char === separator) { row.push(cell); cell = ''; }
    else if (char === '\n' || char === '\r') {
      if (char === '\r' && csvText[i + 1] === '\n') i++;
      row.push(cell); table.push(row); row = []; cell = '';
    } else cell += char;
  }
  if (quoted) throw new Error('The CSV contains an unterminated quoted value.');
  if (cell !== '' || row.length) { row.push(cell); table.push(row); }
  return table.filter((values) => values.some((value) => value !== ''));
}

export function parseAndProfileCsv(csvText) {
  const separator = detectSeparator(csvText);
  const [headers = [], ...data] = parseCsv(stripBom(csvText), separator);
  if (!headers.length) throw new Error('The CSV has no header row.');
  if (new Set(headers).size !== headers.length) throw new Error('The CSV contains duplicate column names.');
  const malformed = data.findIndex((values) => values.length !== headers.length);
  if (malformed !== -1) throw new Error(`CSV row ${malformed + 2} has ${data[malformed].length} values; expected ${headers.length}.`);
  const rows = data.map((values) => Object.fromEntries(headers.map((header, i) => [header, values[i]])));
  const profiles = headers.map((name) => {
    const values = rows.map((r) => String(r[name] ?? '').trim());
    const nonEmpty = values.filter(Boolean);
    return { name, nonEmpty: nonEmpty.length, blank: values.length - nonEmpty.length,
      distinct: new Set(nonEmpty).size, examples: [...new Set(nonEmpty)].slice(0, 5) };
  });
  return { headers, rows, profiles, separator };
}

export function previewTransform(column, rows, limit = 5) {
  return rows.slice(0, limit).map((row) => ({
    source: row[column.source] ?? '',
    values: applyColumn(column, row).map((n) => n.value),
  }));
}

// Distinct source values are far more informative than the first N rows: they
// show the analyst the shape of what they are actually transforming. A blank is
// worth a slot of its own, because empty handling differs per transform.
export function sampleRowsForColumn(rows, source, limit = 6) {
  const distinct = new Map();
  for (const row of rows) {
    const value = String(row[source] ?? '').trim();
    if (!distinct.has(value)) distinct.set(value, row);
  }
  const picked = [...distinct.keys()].slice(0, limit);
  if (picked.length && distinct.has('') && !picked.includes('')) picked[picked.length - 1] = '';
  return picked.map((value) => distinct.get(value));
}

// The value after every step, so a chain can be read as the hops it performs.
// Runs each prefix through the real applyColumn rather than reimplementing the
// chain, so a preview can never diverge from what ingest will write.
export function traceColumn(column, row) {
  const steps = transformSteps(column.transform);
  const trace = [];
  let values = [];
  for (let i = 0; i < steps.length; i++) {
    try {
      values = applyColumn({ ...column, transform: steps.slice(0, i + 1) }, row).map((n) => n.value);
    } catch (error) {
      trace.push({ fn: steps[i].fn, values: [], error: error.message });
      return { source: String(row[column.source] ?? ''), steps: trace, values: [], error: error.message };
    }
    trace.push({ fn: steps[i].fn, values });
  }
  return { source: String(row[column.source] ?? ''), steps: trace, values, error: null };
}

const VALID_IDENTIFIES = new Set(['alone', 'combined', 'no']);
const VALID_SENSITIVITY = new Set(['personal', 'confidential', 'none']);
const VALID_TREATMENT = new Set(['drop', 'coarsen', 'isolate', 'open']);
const VALID_TRANSFORMS = new Set(['const', 'direct', 'mapValues', 'split', 'dateBucket', 'bucket', 'rules']);
const VALID_OUTPUTS = new Set(['literal', 'iri', 'typed']);

// Plain-language names for the palette, so the table and the drawer call a
// transform the same thing.
export const TRANSFORM_LABELS = {
  direct: 'Use the value as it is',
  mapValues: 'Match values',
  split: 'Split into several values',
  dateBucket: 'Group dates by period',
  bucket: 'Group numbers into bands',
  rules: 'Apply ordered rules',
  const: 'Always the same value',
};

// The label vocabulary in plain words. The stored values stay as they are — they
// are the method's own terms and appear in the saved JSON — but nothing should
// make an analyst guess what "alone" or "coarsen" is claiming.
export const IDENTIFIES_LABELS = { alone: 'on its own', combined: 'with other columns', no: 'no' };
export const SENSITIVITY_LABELS = { personal: 'personal', confidential: 'confidential', none: 'not sensitive' };
export const TREATMENT_LABELS = {
  drop: 'not published at all',
  coarsen: 'published only once coarsened',
  isolate: 'published on its own, never combined',
  open: 'published freely',
};

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

function validateStep(step, at, first) {
  if (!isPlainObject(step)) return [`${at} must be an object`];
  if (!VALID_TRANSFORMS.has(step.fn)) return [`${at}.fn is unknown`];
  const errors = [];
  // `const` ignores whatever reaches it, so anywhere but first it would silently
  // replace the chain's work with a constant, once per incoming value.
  if (step.fn === 'const' && !first) errors.push(`${at} must be the first step: it ignores its input`);
  if (step.output !== undefined && !VALID_OUTPUTS.has(step.output)) errors.push(`${at}.output must be literal, iri, or typed`);
  if (step.output === 'typed' && !step.datatype) errors.push(`${at}.datatype is required when output is typed`);
  switch (step.fn) {
    case 'const':
      if (typeof step.value !== 'string' || !step.value) errors.push(`${at}.value is required`);
      break;
    case 'mapValues':
      if (!isPlainObject(step.map)) errors.push(`${at}.map must be an object of source → published values`);
      else if (!Object.keys(step.map).length && step.default === 'drop') errors.push(`${at} would drop every value: add a match or a fallback`);
      if (step.default !== undefined && typeof step.default !== 'string') errors.push(`${at}.default must be a value or "drop"`);
      break;
    case 'split':
      if (typeof step.delimiter !== 'string' || !step.delimiter) errors.push(`${at}.delimiter is required`);
      break;
    case 'dateBucket':
      if (!['year', 'quarter'].includes(step.granularity)) errors.push(`${at}.granularity must be year or quarter`);
      break;
    case 'bucket': {
      if (step.parse !== undefined && !['number', 'duration'].includes(step.parse)) errors.push(`${at}.parse must be number or duration`);
      const edges = step.edges;
      const usable = Array.isArray(edges) && edges.length && edges.every((edge) => Number.isFinite(edge));
      if (!usable) errors.push(`${at}.edges must be a non-empty list of numbers`);
      else if (edges.some((edge, i) => i && edge <= edges[i - 1])) errors.push(`${at}.edges must increase`);
      if (!Array.isArray(step.labels)) errors.push(`${at}.labels must be a list`);
      else if (usable && step.labels.length !== edges.length + 1) errors.push(`${at} needs one more label than edges`);
      break;
    }
    case 'rules': {
      if (!Array.isArray(step.rules) || !step.rules.length) { errors.push(`${at}.rules must be a non-empty list`); break; }
      for (const [j, rule] of step.rules.entries()) {
        const path = `${at}.rules[${j}]`;
        if (typeof rule.then !== 'string' || !rule.then) errors.push(`${path}.then is required`);
        if (!Array.isArray(rule.when) || !rule.when.length) { errors.push(`${path}.when must be a non-empty list`); continue; }
        for (const [k, condition] of rule.when.entries()) {
          if (!condition.col) errors.push(`${path}.when[${k}].col is required`);
          if (!['eq', 'neq'].includes(condition.op)) errors.push(`${path}.when[${k}].op must be eq or neq`);
          if (typeof condition.value !== 'string') errors.push(`${path}.when[${k}].value is required`);
        }
      }
      break;
    }
    default: break; // direct takes no arguments
  }
  return errors;
}

export function validateTransform(transform, at = 'transform') {
  const steps = transformSteps(transform);
  if (!steps.length) return [`${at} needs at least one step`];
  return steps.flatMap((step, i) => validateStep(step, steps.length > 1 ? `${at}[${i}]` : at, i === 0));
}

// Not errors — the chain runs — but almost certainly not what was intended.
export function transformWarnings(transform) {
  const steps = transformSteps(transform);
  return steps.slice(0, -1).flatMap((step, i) => (step.output === 'iri' || step.output === 'typed'
    ? [`Step ${i + 1} declares an output type, but only the last step's type is published.`]
    : []));
}

export function validateMapping(mapping, catalog = null) {
  const errors = [];
  if (!mapping || typeof mapping !== 'object') return ['mapping is required'];
  if (!mapping.connector) errors.push('connector is required');
  if (!mapping.records || !Object.keys(mapping.records).length) errors.push('at least one record type is required');
  if (!Array.isArray(mapping.columns)) errors.push('columns must be an array');
  if (!Array.isArray(mapping.drop)) errors.push('drop must be an array');
  const catalogTerms = new Set(catalog?.terms?.map((t) => t.id) ?? []);
  for (const [i, c] of (mapping.columns ?? []).entries()) {
    const at = `columns[${i}]`;
    if (!c.source) errors.push(`${at}.source is required`);
    if (!c.target) errors.push(`${at}.target is required`);
    if (c.record !== 'shared' && !mapping.records?.[c.record]) errors.push(`${at}.record is unknown`);
    errors.push(...validateTransform(c.transform, `${at}.transform`));
    if (c.record !== 'shared') {
      if (!VALID_IDENTIFIES.has(c.identifies)) errors.push(`${at}.identifies is invalid`);
      if (!VALID_SENSITIVITY.has(c.sensitivity)) errors.push(`${at}.sensitivity is invalid`);
      if (!VALID_TREATMENT.has(c.treatment)) errors.push(`${at}.treatment is invalid`);
      if (c.term && catalogTerms.size && !catalogTerms.has(c.term) && !c.manualTerm) errors.push(`${at}.term is not in the catalog`);
    }
  }
  return errors;
}

// With the institution's datasets.json, a cube must count terms of one dataset
// only (lib/datasets.js). Without it the rule is not checked here.
export function validateAggregations(agg, mapping, datasetsDoc = null) {
  const errors = [];
  const resolved = datasetsDoc ? resolveDatasets(datasetsDoc, mapping) : null;
  // false when the period values are separate samples (survey rounds) rather
  // than parts of a whole (quarters): a comparison then never sums them.
  if (agg?.periodsAddUp != null && typeof agg.periodsAddUp !== 'boolean') errors.push('periodsAddUp must be true or false');
  if (!Array.isArray(agg?.aggregates)) errors.push('aggregates must be an array');
  const dimensions = new Set((mapping?.columns ?? []).map((c) => c.target));
  const ids = new Set();
  for (const [i, a] of (agg?.aggregates ?? []).entries()) {
    const at = `aggregates[${i}]`;
    if (!a.id) errors.push(`${at}.id is required`);
    if (ids.has(a.id)) errors.push(`${at}.id is duplicated`); else ids.add(a.id);
    if (!['single', 'combined'].includes(a.kind)) errors.push(`${at}.kind is invalid`);
    const reducer = a.reducer ?? 'count';
    const measure = a.measure ?? agg?.measure;
    if (!['count', 'sum'].includes(reducer)) errors.push(`${at}.reducer is invalid`);
    if (reducer === 'sum' && !measure) errors.push(`${at}.measure is required for reducer sum`);
    else if (reducer === 'sum' && !dimensions.has(measure)) errors.push(`${at} uses unknown measure ${measure}`);
    if (a.suppressMeasureBelow !== undefined
      && (!Number.isFinite(a.suppressMeasureBelow) || a.suppressMeasureBelow < 0))
      errors.push(`${at}.suppressMeasureBelow must be a non-negative number`);
    if (a.suppressMeasureBelow !== undefined && reducer !== 'sum')
      errors.push(`${at}.suppressMeasureBelow requires reducer sum`);
    if (!Array.isArray(a.dimensions) || !a.dimensions.length) errors.push(`${at}.dimensions is required`);
    if (new Set(a.dimensions).size !== a.dimensions.length) errors.push(`${at}.dimensions contains duplicates`);
    // `kind` is not decoration: it is the claim the cube makes about its own
    // shape, and a "single" carrying two dimensions is a cross published under
    // a marginal's name. Only checked once the array itself is sound.
    if (Array.isArray(a.dimensions) && a.dimensions.length) {
      if (a.kind === 'single' && a.dimensions.length !== 1)
        errors.push(`${at}.dimensions must be one dimension for a single aggregate`);
      if (a.kind === 'combined' && a.dimensions.length !== 2)
        errors.push(`${at}.dimensions must be two dimensions for a combined aggregate`);
    }
    for (const d of a.dimensions ?? []) if (!dimensions.has(d)) errors.push(`${at} uses unknown dimension ${d}`);
    if (resolved) {
      const { problem } = datasetOfCube(a, agg, mapping, resolved);
      if (problem) errors.push(`${at} ${problem}`);
    }
  }
  return errors;
}

export function validateDisclosure(mapping, aggregations, catalog = null) {
  return { mapping: validateMapping(mapping, catalog), aggregations: validateAggregations(aggregations, mapping) };
}

// What a changed mapping of one source would take from the cubes, in words, or
// []. A cube counts a term; the columns of this source that fill it must still
// do so, and where they grouped their values before they must still group
// them, or the cube would count fewer records, or raw values (docs/ref-
// belonging-plan.md, step 7.1). A sum cube's measure only has to stay filled.
// Only what this change takes away is refused: a cube that already counted an
// ungrouped term keeps doing so.
export function mappingUseProblems(before, after, aggregations) {
  const kept = (mapping, term) => (mapping?.columns ?? [])
    .filter((c) => c.record !== 'shared' && c.target === term && c.treatment !== 'drop');
  const grouped = (columns) => columns.length > 0 && columns.every((c) => coarsens(c.transform));
  const named = (columns) => columns.map((c) => `“${c.source}”`).join(', ');
  const problems = [];
  for (const cube of aggregations?.aggregates ?? []) {
    const reducer = cube.reducer ?? 'count';
    const measure = reducer === 'sum' ? cube.measure ?? aggregations.measure : null;
    for (const term of [...(cube.dimensions ?? []), ...(measure ? [measure] : [])]) {
      const was = kept(before, term);
      const now = kept(after, term);
      if (!was.length) continue;
      if (!now.length) {
        problems.push(`${named(was)} fills ${term}, which the cube ${cube.id} counts: keep sharing it, or remove the cube first.`);
      } else if (term !== measure && grouped(was) && !grouped(now)) {
        problems.push(`${named(now)} fills ${term}, which the cube ${cube.id} counts, so its values must stay grouped.`);
      }
    }
  }
  return problems;
}

